import {
  compose,
  withHandlers,
  withPropsOnChange,
  withState,
} from 'recompose';
import { connect } from 'react-redux';
import { formValueSelector, reduxForm, change } from 'redux-form';
import { partial } from 'lodash';
import Joi from 'joi-full';
import { prop } from 'ramda';

import withToggle from '../../../../common/hoc/withToggle';
import withNotifications from '../../../../common/withNotifications';
import { getActiveShop, getActiveType } from '../../../../auth/selectors';
import { DEFAULT_AUTO_TIPS_FEE_PERCENT, DEFAULT_COMMISSION_SPLIT_FEE_PERCENT } from '../../../shops/constants';
import {
  commissionSplitsTurnedOff,
  dailyPayrollTurnedOff,
  getAutoPayoutUpdate,
  getEligibilityStepsStatus,
  getInitialAutoPayoutValues,
  getOverallEligibilityStatus,
  isAutoPayoutEnabled,
  isAutoPayoutPaused,
} from '../utils';
import withLoadedEligibilityStatus from '../withLoadedEligibilityStatus';
import { userUpdateShop } from '../../../shops/actions';
import confirmModal from '../../../../common/confirmModal';
import { syncValidateData } from '../../../../common/validator';
import { ELIGIBILITY_STEP_STATUS, END_OF_PAY_PERIOD_DAY_OPTIONS } from '../constants';
import { canUserModifyAutoPayoutSettings } from '../../../../common/helpers/users';
import withPermissions from '../../../../auth/utils/withPermissions';

const AUTO_PAYOUT_FORM = 'autoPayoutForm';

const schema = Joi.object().keys({
  dailyPayroll: Joi.boolean().required(),
  dailyPayrollTipFee: Joi.number().min(0).max(100).optional(),
  commissionSplits: Joi.boolean().required(),
  commissionSplitsPercentFee: Joi.number().min(0).max(100).optional(),
  commissionSplitsShopPaysFees: Joi.boolean().required(),
  commissionSplitsCashToBarbersServices: Joi.boolean().required(),
  commissionSplitsCashToBarbersProducts: Joi.boolean().required(),
  commissionPayoutDayOfWeek: Joi.when('commissionSplits', {
    is: true,
    then: Joi.number().valid(END_OF_PAY_PERIOD_DAY_OPTIONS.map(prop('value')))
      .required()
      .label('End of Pay Period Day')
      .options({
        language: { number: { base: ' value  is required.' } },
      }),
  }),
});

const validate = values => syncValidateData(values, schema);

const form = reduxForm({
  form: AUTO_PAYOUT_FORM,
  validate,
  enableReinitialize: true,
});

const selector = formValueSelector(AUTO_PAYOUT_FORM);

export default compose(
  withLoadedEligibilityStatus,
  withPermissions,
  withNotifications,
  withToggle('howToEnable', true),
  withState('isSaving', 'setIsSaving', false),
  connect(
    (state, ownProps) => {
      const shop = getActiveShop(state);
      const eligibilitySteps = getEligibilityStepsStatus(ownProps.autoPayoutEligibilityStatus);
      const overallEligibilityStatus = getOverallEligibilityStatus(eligibilitySteps);

      return {
        shop,
        isShopViewActive: getActiveType(state) === 'shop',
        eligibilitySteps,
        overallEligibilityStatus,
        barbersWithIssues: ownProps.autoPayoutEligibilityStatus?.barbersWithIssues || [],
        isAutoPayoutPaused: isAutoPayoutPaused(ownProps.autoPayoutEligibilityStatus, shop),
        isAutoPayoutEnabled: isAutoPayoutEnabled(shop),
        dailyPayroll: selector(state, 'dailyPayroll'),
        commissionSplits: selector(state, 'commissionSplits'),
        commissionSplitsShopPaysFees: selector(state, 'commissionSplitsShopPaysFees'),
        dailyPayrollFee: shop.dailyPayrollTipFee === null ? DEFAULT_AUTO_TIPS_FEE_PERCENT : shop.dailyPayrollTipFee,
        commissionSplitsFee: shop.commissionSplitsPercentFee === null
          ? DEFAULT_COMMISSION_SPLIT_FEE_PERCENT : shop.commissionSplitsPercentFee,
        initialValues: getInitialAutoPayoutValues(shop),
        isAutoPayoutSettingsEditable: canUserModifyAutoPayoutSettings(ownProps.permissions) && (
          overallEligibilityStatus === ELIGIBILITY_STEP_STATUS.DONE
          || (shop.dailyPayroll || shop.commissionSplits)
        ),
      };
    },
    {
      change: partial(change, AUTO_PAYOUT_FORM),
      updateAutoPayoutSettings: userUpdateShop,
    },
  ),
  withPropsOnChange(['dailyPayroll'], (props) => {
    if (props.dailyPayroll && props.commissionSplits) {
      props.change('commissionSplits', false);
    }
  }),
  withPropsOnChange(['commissionSplits'], (props) => {
    if (props.dailyPayroll && props.commissionSplits) {
      props.change('dailyPayroll', false);
    }
    if (!props.commissionSplits) {
      props.change('commissionSplitsShopPaysFees', false);
      props.change('commissionSplitsCashToBarbersServices', false);
      props.change('commissionSplitsCashToBarbersProducts', false);
    }
  }),
  withHandlers({
    onSubmit: props => async (data) => {
      props.setIsSaving(true);

      if (dailyPayrollTurnedOff(data, props.initialValues)) {
        const confirmed = await confirmModal({
          type: 'danger',
          title: 'Disable Daily Shop Payouts?',
          message: 'The current automatic payout will process, '
            + 'but will be disabled immediately after processing the current payout.',
        });

        if (!confirmed) {
          props.setIsSaving(false);
          return false;
        }
      }

      if (commissionSplitsTurnedOff(data, props.initialValues)) {
        const confirmed = await confirmModal({
          type: 'danger',
          title: 'Disable Auto Commission Splits?',
          message: 'The current automatic payout will process, '
            + 'but will be disabled immediately after processing the current payout.',
        });

        if (!confirmed) {
          props.setIsSaving(false);
          return false;
        }
      }

      const update = getAutoPayoutUpdate(data);

      props.updateAutoPayoutSettings(props.shop.id, update, props.shop)
        .then(() => {
          props.setIsSaving(false);

          props.addNotification({
            title: 'Success',
            level: 'success',
            message: 'Changes saved successfully',
          });
        })
        .catch(() => {
          props.setIsSaving(false);
        });
    },
  }),
  form,
);
